import React, { memo, useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

/** Stable references — inline objects on <Canvas> would change every App re-render and re-configure R3F. */
const CANVAS_STYLE: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: -1,
  pointerEvents: "none",
};

const GL_PROPS = { antialias: true, alpha: false as const };

const FRAGMENT_SHADER = `
    uniform float u_time;
    uniform vec2 u_mouse;
    varying vec2 vUv;

    float circle(vec2 uv, vec2 p, float r) {
      return smoothstep(r, r - 0.01, length(uv - p));
    }

    void main() {
      vec2 uv = vUv;

      vec2 center = vec2(0.5, 0.5);
      vec2 dir = normalize(u_mouse - center + 0.001);
      float flowCoord = dot(uv - center, vec2(-dir.y, dir.x));
      float wave = sin(flowCoord * 14.0);
      uv += dir * wave * 0.05;

      vec2 gridUv = uv * 80.0;
      vec2 gv = fract(gridUv) - 0.5;
      vec2 id = floor(gridUv);

      float t = u_time * 0.8;
      float pulse = 0.035 + 0.015 * sin(t + id.x * 0.7 + id.y * 0.9);

      float d = circle(gv, vec2(0.0), pulse);

      vec3 colA = vec3(0.02, 0.03, 0.08);
      vec3 colB = vec3(1.00, 0.58, 0.23);
      vec3 colC = vec3(0.40, 0.76, 1.00);

      float mixKey = sin(id.x * 0.6 + id.y * 0.3 + t * 0.4) * 0.5 + 0.5;
      vec3 dotColor = mix(colB, colC, mixKey);

      vec3 bg = colA;

      vec3 color = mix(bg, dotColor, d * 0.65);

      float vignette = smoothstep(0.95, 0.45, length(vUv - 0.5));
      color *= vignette;

      gl_FragColor = vec4(color, 1.0);
    }
  `;

const VERTEX_SHADER = `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = vec4(position.xy, 0.0, 1.0);
    }
  `;

const DotPlane: React.FC = () => {
  const meshRef = useRef<THREE.Mesh>(null);
  const mouse = useRef<[number, number]>([0.5, 0.5]);

  const uniforms = useMemo(
    () => ({
      u_time: { value: 0 },
      u_mouse: { value: new THREE.Vector2(0.5, 0.5) },
    }),
    []
  );

  useEffect(() => {
    const handleMove = (e: PointerEvent) => {
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;
      mouse.current = [x, 1 - y];
    };
    window.addEventListener("pointermove", handleMove);
    return () => window.removeEventListener("pointermove", handleMove);
  }, []);

  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    if (!meshRef.current) return;
    const material = meshRef.current.material as THREE.ShaderMaterial;
    material.uniforms.u_time.value = t;
    material.uniforms.u_mouse.value.set(mouse.current[0], mouse.current[1]);
  });

  return (
    <mesh ref={meshRef}>
      <planeGeometry args={[2, 2]} />
      <shaderMaterial
        fragmentShader={FRAGMENT_SHADER}
        vertexShader={VERTEX_SHADER}
        transparent={false}
        uniforms={uniforms}
      />
    </mesh>
  );
};

const DotPlaneMemo = memo(DotPlane);

function DotShaderBackgroundInner() {
  return (
    <Canvas
      camera={{ position: [0, 0, 1] }}
      style={CANVAS_STYLE}
      gl={GL_PROPS}
      frameloop="always"
    >
      <DotPlaneMemo />
    </Canvas>
  );
}

/** Memoized so parent (App) state updates don’t re-configure the Canvas / disrupt the rAF loop. */
export const DotShaderBackground = memo(DotShaderBackgroundInner);
